import { useCallback, useEffect, useState } from 'react';
import { ALL_ACCOUNTS, cleanAccountName, getMyPerformance } from '../../components/dashboard/myData/myDataApi';
import { PALETTE } from '../../components/dashboard/myData/myWidgetUi';
import { getMyStrategySeries, listMyStrategies } from '../../services/personalStrategiesApi';

/*
 * Everything a self user can analyze: their connected accounts plus their own
 * strategies ("My strategies"). Loaded once over all time; pages trim to a
 * period on the client.
 */
export default function usePersonalSources() {
  const [state, setState] = useState({ loading: true, error: '', requiresPro: false, sources: [], strategies: [], missing: [] });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: '' }));
      try {
        const [perf, list] = await Promise.all([
          getMyPerformance(ALL_ACCOUNTS, 'All'),
          listMyStrategies().catch((err) => { if (err.requiresPro) throw err; return { strategies: [] }; }),
        ]);
        const strategies = list.strategies || [];
        const series = await Promise.all(strategies.map((s) => getMyStrategySeries(s.id).catch(() => null)));
        const accounts = perf.perAccount.map((a) => ({
          key: `acct:${a.id}`,
          kind: 'account',
          name: cleanAccountName(a.name),
          broker: a.broker,
          points: a.points,
        }));
        const mine = series.filter((s) => s && s.points?.length > 1).map((s) => ({
          key: `strat:${s.id}`,
          kind: 'strategy',
          id: s.id,
          name: s.name,
          points: s.points,
          trades: s.trades || [],
        }));
        const sources = [...accounts, ...mine].map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
        if (live) setState({ loading: false, error: '', requiresPro: false, sources, strategies, missing: perf.missing || [] });
      } catch (err) {
        if (live) setState({ loading: false, error: err?.message || 'Couldn’t load your data.', requiresPro: Boolean(err?.requiresPro), sources: [], strategies: [], missing: [] });
      }
    })();
    return () => { live = false; };
  }, [nonce]);

  return { ...state, reload };
}
